'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { connectCustomer, runBuyFrame } from '@/lib/moonpay/frames';
import { moonpayApi, type MoonpayKycState } from '@/services/api/moonpay.api';
import { useMoonpayCustomer } from '@/services/query';
import { MoonpayKycStep } from './MoonpayKycStep';
import { DepositHeader } from './subcomponents/deposit';

import { alpha3ForCountry } from '@/constants';

/**
 * HEADLESS deposit (Customer-API countries only). We own KYC capture; MoonPay
 * only runs the OTP-login + buy frames. No hosted onboarding, no fallback — the
 * country was already confirmed headless by the selector.
 */

type Step =
  | 'amount'
  | 'connecting'
  | 'kyc'
  | 'kyc_challenge'
  | 'verifying'
  | 'quote'
  | 'buying'
  | 'success'
  | 'error';

interface Props {
  /** ISO alpha-2 residence country (headless-supported). */
  country: string;
  /** Local fiat for the country (ISO 4217). */
  fiat: string;
  onBack: () => void;
  onClose: () => void;
  balance?: number;
}

const QUICK = [200, 500, 1000];
const inputCls =
  'w-full border-none bg-transparent p-0 text-3xl font-bold text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]';
const TERMS_URL = 'https://www.moonpay.com/legal/terms_of_use';
const PRIVACY_URL = 'https://www.moonpay.com/legal/privacy_policy';

export const MoonpayHeadlessDeposit = ({ country, fiat, onBack, onClose, balance = 0 }: Props) => {
  const { t } = useT();
  const a3 = alpha3ForCountry(country) || country;

  const [step, setStep] = useState<Step>('amount');
  const [amount, setAmount] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const [creds, setCreds] = useState<{ accessToken: string; clientToken: string } | null>(null);
  const [kyc, setKyc] = useState<MoonpayKycState | null>(null);
  const [challengeUrl, setChallengeUrl] = useState<string | null>(null);
  const [quote, setQuote] = useState<any>(null);
  const [limits, setLimits] = useState<{ min?: number; max?: number }>({});
  const [verifyTimedOut, setVerifyTimedOut] = useState(false);

  const connectRef = useRef<HTMLDivElement>(null);
  const buyRef = useRef<HTMLDivElement>(null);
  // Captured the moment the user accepts MoonPay's terms (clicks Continue).
  const termsAcceptedAtRef = useRef<string | null>(null);

  // Route to the correct step from a KYC state (after connect, submit, poll).
  const routeKyc = useCallback(
    (state: MoonpayKycState) => {
      setKyc(state);
      const chal = getChallengeUrl(state);
      if (state.kycStatus === 'verified') {
        setStep('quote');
      } else if (chal) {
        setChallengeUrl(chal);
        setStep('kyc_challenge');
      } else if (state.status === 'unavailable' || state.kycStatus === 'rejected') {
        setError(
          t('moonpay.errors.rejected', {
            defaultValue: 'Verification could not be completed. Please contact support.',
          }),
        );
        setStep('error');
      } else if (state.status === 'verifying' || state.kycStatus === 'under_review') {
        setStep('verifying');
      } else {
        // collecting / not_created → capture (more) data in our own screens.
        setStep('kyc');
      }
    },
    [t],
  );

  // Poll ONLY while verifying or a challenge is open; re-route each tick.
  const polling = step === 'verifying' || step === 'kyc_challenge';
  const { data: polledKyc } = useMoonpayCustomer({ enabled: polling, poll: polling });
  useEffect(() => {
    if (polling && polledKyc) routeKyc(polledKyc);
  }, [polling, polledKyc, routeKyc]);

  // Never spin forever on verifying.
  useEffect(() => {
    if (step !== 'verifying') {
      setVerifyTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setVerifyTimedOut(true), 75_000);
    return () => clearTimeout(timer);
  }, [step]);

  // Deposit min/max from the public /v3/currencies catalog (pre-auth, server key).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const l = await moonpayApi.getLimits(fiat);
        if (cancelled) return;
        setLimits({
          min: typeof l.minBuyAmount === 'number' ? l.minBuyAmount : undefined,
          max: typeof l.maxBuyAmount === 'number' ? l.maxBuyAmount : undefined,
        });
      } catch {
        /* best-effort — limits are a hint, not a hard gate */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fiat]);

  // ── Connect (session → check/auth frame → link customer → KYC gate) ─────────
  useEffect(() => {
    if (step !== 'connecting' || !connectRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const deviceIp = await moonpayApi.getPublicIp();
        const { sessionToken } = await moonpayApi.createSession(
          deviceIp,
          termsAcceptedAtRef.current || undefined,
        );
        const result = await connectCustomer(sessionToken, connectRef.current!);
        if (cancelled) return;

        if (result.status !== 'active' || !result.credentials) {
          throw new Error(
            result.status === 'unavailable'
              ? t('moonpay.errors.unavailable', {
                  defaultValue: 'MoonPay is not available in your region.',
                })
              : t('moonpay.errors.connect', {
                  defaultValue: 'Could not connect to MoonPay. Please try again.',
                }),
          );
        }

        const nc = {
          accessToken: result.credentials.accessToken,
          clientToken: result.credentials.clientToken,
        };
        setCreds(nc);

        const state = result.customerId
          ? await moonpayApi.linkCustomer(result.customerId)
          : await moonpayApi.getCustomer();
        if (cancelled) return;
        routeKyc(state);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.error || e?.message || 'Connection failed');
        setStep('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, t, routeKyc]);

  // ── Quote (once verified) ───────────────────────────────────────────────────
  useEffect(() => {
    if (step !== 'quote' || !creds) return;
    let cancelled = false;
    (async () => {
      try {
        const qte = await moonpayApi.getBuyQuote({
          accessToken: creds.accessToken,
          amount: Number(amount),
          fiat,
        });
        if (!cancelled) setQuote(qte);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.error || e?.message || 'Could not fetch quote');
          setStep('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, creds, amount, fiat]);

  // ── Buy (buy frame → record) ────────────────────────────────────────────────
  useEffect(() => {
    if (step !== 'buying' || !creds || !quote) return;
    let cancelled = false;
    (async () => {
      try {
        const signature = quote.signature || quote.quote?.signature;
        const tx = await runBuyFrame(creds.clientToken, signature, {
          challengeContainer: buyRef.current,
        });
        if (cancelled) return;
        await moonpayApi.recordDeposit({
          moonpayTransactionId: tx?.id,
          amount: Number(amount),
          fiat,
          status: tx?.status,
        });
        setStep('success');
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.error || e?.message || 'Payment failed');
        setStep('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, creds, quote, amount, fiat]);

  const payout =
    quote?.quoteCurrencyAmount || quote?.destination?.amount || quote?.quote?.quoteCurrencyAmount;
  const amountNum = Number(amount);
  const belowMin = limits.min != null && amountNum > 0 && amountNum < limits.min;
  const aboveMax = limits.max != null && amountNum > limits.max;
  const canContinue = amountNum > 0 && !belowMin && !aboveMax;

  const startConnect = () => {
    setError(null);
    termsAcceptedAtRef.current = new Date().toISOString();
    setStep('connecting');
  };

  return (
    <Box className="flex min-h-[520px] flex-col">
      <DepositHeader
        balance={balance}
        onClose={onClose}
        onBack={step === 'amount' ? onBack : () => setStep('amount')}
        title={t('moonpay.title', { defaultValue: 'Deposit with MoonPay' })}
      />

      {/* Amount */}
      {step === 'amount' && (
        <Box className="flex flex-1 flex-col gap-4">
          <Box className="flex flex-col gap-2 rounded-[24px] bg-[var(--bg-secondary)] p-6 dark:bg-[#F8F9FB10]">
            <Typography className="text-sm font-medium text-[var(--text-muted)]">
              {t('moonpay.youSpend', { defaultValue: 'You spend' })}
            </Typography>
            <Box className="flex items-center gap-1">
              <input
                className={inputCls}
                inputMode="decimal"
                placeholder="0"
                value={amount}
                onChange={(e) => /^\d*\.?\d*$/.test(e.target.value) && setAmount(e.target.value)}
              />
              <Typography className="text-sm font-bold text-[var(--text-muted)]">{fiat}</Typography>
            </Box>
          </Box>
          <Box className="flex gap-2">
            {QUICK.map((v) => (
              <button
                key={v}
                onClick={() => setAmount(String(v))}
                className="flex-1 rounded-xl border border-[var(--border-color)] py-2 text-sm font-bold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-secondary)]"
              >
                {v}
              </button>
            ))}
          </Box>
          {(belowMin || aboveMax) && (
            <Typography className="text-xs font-medium text-[var(--accent-red)]">
              {belowMin
                ? t('moonpay.minAmount', {
                    defaultValue: `Minimum deposit is ${limits.min} ${fiat}.`,
                    min: limits.min,
                    fiat,
                  })
                : t('moonpay.maxAmount', {
                    defaultValue: `Maximum deposit is ${limits.max} ${fiat}.`,
                    max: limits.max,
                    fiat,
                  })}
            </Typography>
          )}
          <Box className="mt-auto flex flex-col gap-3">
            <Typography className="text-center text-[11px] leading-snug text-[var(--text-muted)]">
              {t('moonpay.termsPrefix', { defaultValue: 'By continuing you agree to MoonPay’s' })}{' '}
              <a href={TERMS_URL} target="_blank" rel="noreferrer" className="underline">
                {t('moonpay.terms', { defaultValue: 'Terms' })}
              </a>{' '}
              &{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="underline">
                {t('moonpay.privacy', { defaultValue: 'Privacy Policy' })}
              </a>
              .
            </Typography>
            <button
              disabled={!canContinue}
              onClick={startConnect}
              className="w-full rounded-2xl bg-[var(--primary)] px-4 py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {t('common.continue', { defaultValue: 'Continue' })}
            </button>
          </Box>
        </Box>
      )}

      {/* Connect frame (visible OTP/login) */}
      <Box className={step === 'connecting' ? 'flex flex-1 flex-col' : 'hidden'}>
        <Typography className="mb-3 text-sm text-[var(--text-muted)]">
          {t('moonpay.connecting', { defaultValue: 'Sign in to MoonPay to continue…' })}
        </Typography>
        <Box ref={connectRef} className="flex-1 overflow-hidden rounded-2xl" />
      </Box>

      {/* KYC capture (our own screens) */}
      {step === 'kyc' && kyc && (
        <Box className="flex-1 overflow-y-auto">
          <MoonpayKycStep state={kyc} onSubmitted={routeKyc} country={a3} />
        </Box>
      )}

      {/* KYC challenge — hosted frame MoonPay requires when API data isn't enough */}
      {step === 'kyc_challenge' && challengeUrl && (
        <Box className="flex flex-1 flex-col">
          <Typography className="mb-2 text-sm text-[var(--text-muted)]">
            {t('moonpay.challenge', {
              defaultValue: 'One more step — complete the verification below.',
            })}
          </Typography>
          <iframe
            title="MoonPay verification"
            src={challengeUrl}
            allow="camera; microphone; accelerometer; gyroscope; payment"
            className="min-h-[520px] w-full flex-1 rounded-2xl border-none"
          />
        </Box>
      )}

      {/* Verifying */}
      {step === 'verifying' && (
        <Box className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
          {!verifyTimedOut ? (
            <>
              <Box className="h-10 w-10 animate-spin rounded-full border-4 border-[var(--border-color)] border-t-[var(--primary)]" />
              <Typography className="text-sm font-medium text-[var(--text-primary)]">
                {t('moonpay.verifying', {
                  defaultValue: 'Verifying your details with our partner…',
                })}
              </Typography>
              <Typography className="text-xs text-[var(--text-muted)]">
                {t('moonpay.verifyingHint', { defaultValue: 'This usually takes a few moments.' })}
              </Typography>
            </>
          ) : (
            <>
              <Box className="text-3xl">⏳</Box>
              <Typography className="text-sm font-medium text-[var(--text-primary)]">
                {t('moonpay.verifyTimeout', {
                  defaultValue: 'This is taking longer than expected.',
                })}
              </Typography>
              <Typography className="max-w-xs text-xs text-[var(--text-muted)]">
                {t('moonpay.verifyTimeoutHint', {
                  defaultValue:
                    'MoonPay may still be reviewing. You can keep waiting or close and check back later.',
                })}
              </Typography>
              <Box className="mt-2 flex gap-2">
                <button
                  onClick={() => setVerifyTimedOut(false)}
                  className="rounded-2xl border border-[var(--border-color)] px-5 py-2.5 text-sm font-bold text-[var(--text-primary)]"
                >
                  {t('common.keepWaiting', { defaultValue: 'Keep waiting' })}
                </button>
                <button
                  onClick={onClose}
                  className="rounded-2xl bg-[var(--primary)] px-5 py-2.5 text-sm font-bold text-white"
                >
                  {t('common.close', { defaultValue: 'Close' })}
                </button>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Quote / confirm */}
      {step === 'quote' && (
        <Box className="flex flex-1 flex-col gap-4">
          <Box className="flex flex-col gap-3 rounded-[24px] bg-[var(--bg-secondary)] p-6 dark:bg-[#F8F9FB10]">
            <Row
              label={t('moonpay.youSpend', { defaultValue: 'You spend' })}
              value={`${amount} ${fiat}`}
            />
            <Row
              label={t('moonpay.youGet', { defaultValue: 'You get' })}
              value={
                payout
                  ? `${Number(payout).toLocaleString(undefined, { maximumFractionDigits: 4 })} USDC`
                  : '…'
              }
            />
          </Box>
          <Box className="mt-auto">
            <button
              disabled={!quote}
              onClick={() => setStep('buying')}
              className="w-full rounded-2xl bg-[var(--primary)] px-4 py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {t('moonpay.pay', { defaultValue: 'Continue to payment' })}
            </button>
          </Box>
        </Box>
      )}

      {/* Buying (headless buy frame + any 3DS challenge) */}
      <Box className={step === 'buying' ? 'flex flex-1 flex-col' : 'hidden'}>
        <Typography className="mb-3 text-sm text-[var(--text-muted)]">
          {t('moonpay.processing', { defaultValue: 'Processing your payment…' })}
        </Typography>
        <Box ref={buyRef} className="flex-1 overflow-hidden rounded-2xl" />
      </Box>

      {/* Success */}
      {step === 'success' && (
        <Box className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <Box className="bg-[var(--accent-green)]/15 flex h-14 w-14 items-center justify-center rounded-full text-2xl">
            ✅
          </Box>
          <Typography className="text-base font-bold text-[var(--text-primary)]">
            {t('moonpay.success', { defaultValue: 'Deposit started!' })}
          </Typography>
          <Typography className="max-w-xs text-xs text-[var(--text-muted)]">
            {t('moonpay.successHint', {
              defaultValue:
                'Your USDC will arrive in your wallet shortly. You can close this window.',
            })}
          </Typography>
          <button
            onClick={onClose}
            className="mt-2 rounded-2xl bg-[var(--primary)] px-6 py-3 text-sm font-bold text-white"
          >
            {t('common.done', { defaultValue: 'Done' })}
          </button>
        </Box>
      )}

      {/* Error */}
      {step === 'error' && (
        <Box className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <Box className="text-3xl">⚠️</Box>
          <Typography className="max-w-xs text-sm text-[var(--accent-red)]">{error}</Typography>
          <button
            onClick={() => {
              setError(null);
              setStep('amount');
            }}
            className="rounded-2xl border border-[var(--border-color)] px-6 py-3 text-sm font-bold text-[var(--text-primary)]"
          >
            {t('common.tryAgain', { defaultValue: 'Try again' })}
          </button>
        </Box>
      )}
    </Box>
  );
};

/** Extract the hosted challenge URL from a KYC state (string or { url } shapes). */
function getChallengeUrl(state?: MoonpayKycState | null): string | null {
  const c: any = state?.challenge;
  if (!c) return null;
  if (typeof c === 'string') return c;
  return c.url || c.challengeUrl || c.href || null;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Box className="flex items-center justify-between">
      <Typography className="text-sm text-[var(--text-muted)]">{label}</Typography>
      <Typography className="text-sm font-bold text-[var(--text-primary)]">{value}</Typography>
    </Box>
  );
}
